(function () {
  const collectionRestoreKey = 'luredolls_collection_restore_target';
  const collectionSnapshotKey = 'luredolls_collection_restore_snapshot';
  const productReturnKey = 'luredolls_product_return_target';
  const collectionSnapshotVersion = 2;
  const collectionRestoreMaxAge = 30 * 60 * 1000;
  const collectionSnapshotMaxLength = 2 * 1024 * 1024;
  const collectionSnapshotDebounceDelay = 250;
  const collectionSnapshotFallbackDelay = 1500;
  const productCardSelector = '[data-product-click-item]';
  const productListRootSelector = '#ProductGridContainer, #product-grid, infinite-scroll, .collection, .product-grid, slider-component';
  const impressionStartDelay = 3500;
  const impressionBatchDelay = 250;
  const observedProductCards = new WeakSet();
  const observedProductListRoots = new WeakSet();
  const reportedImpressions = new Set();
  const queuedImpressions = new Map();
  let impressionFlushTimer = 0;
  let productImpressionObserver = null;
  let productMutationObserver = null;
  let productImpressionTrackingStarted = false;
  let collectionSnapshotGeneration = 0;
  let collectionSnapshotTimer = 0;
  let collectionSnapshotReady = false;
  let latestCollectionSnapshotMetadata = null;

  function isModifiedClick(event) {
    return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  }

  function collectionKeyFromUrl(urlValue) {
    const url = new URL(urlValue, window.location.origin);
    if (url.origin !== window.location.origin) return '';

    url.searchParams.delete('page');
    url.searchParams.delete('section_id');

    const search = url.searchParams.toString();
    return `${url.pathname}${search ? `?${search}` : ''}`;
  }

  function collectionKeyFromLocation() {
    return collectionKeyFromUrl(window.location.href);
  }

  function homeKeyFromUrl(urlValue) {
    const url = new URL(urlValue, window.location.origin);
    if (url.origin !== window.location.origin) return '';

    return url.pathname || '/';
  }

  function finiteNumber(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function currentInfiniteScrollPage(infiniteScroll) {
    return Math.max(
      finiteNumber(infiniteScroll.currentPage, finiteNumber(infiniteScroll.dataset.currentPage, 1)),
      1
    );
  }

  function isCollectionPage() {
    return document.documentElement.dataset.pageType === 'collection';
  }

  function isHomepage() {
    return document.documentElement.dataset.pageType === 'index';
  }

  function persistProductReturnContext(target) {
    try {
      window.sessionStorage.setItem(productReturnKey, JSON.stringify(target));
    } catch (error) {}
  }

  function collectionGridItemCount(grid) {
    return grid ? grid.childElementCount : 0;
  }

  function buildCollectionRestoreTarget(card, grid, infiniteScroll) {
    const productId = card.dataset.productId;
    if (!productId) return null;

    const item = card.closest('.grid__item');
    const itemIndex = item ? Array.prototype.indexOf.call(grid.children, item) : -1;
    const targetIndex = finiteNumber(item?.dataset.collectionIndex, itemIndex);
    const currentPage = currentInfiniteScrollPage(infiniteScroll);
    const loadedProductCount = collectionGridItemCount(grid);
    const productsPerPage = currentPage > 0
      ? Math.max(Math.round(loadedProductCount / currentPage), 1)
      : Math.max(loadedProductCount, 1);
    const targetPage = Math.max(
      finiteNumber(item?.dataset.collectionPage, Math.ceil((targetIndex + 1) / productsPerPage)),
      1
    );

    return {
      productId,
      collectionKey: collectionKeyFromLocation(),
      collectionTitle: document.documentElement.dataset.collectionTitle || '',
      viewportTop: Math.round(card.getBoundingClientRect().top),
      scrollY: Math.round(window.scrollY || 0),
      currentPage,
      totalPages: Math.max(finiteNumber(infiniteScroll.totalPages, finiteNumber(infiniteScroll.dataset.totalPages, currentPage)), currentPage),
      loadedProductCount,
      targetIndex: targetIndex >= 0 ? targetIndex : undefined,
      targetPage,
      documentHeight: Math.max(
        finiteNumber(latestCollectionSnapshotMetadata?.documentHeight),
        Math.round((window.scrollY || 0) + window.innerHeight)
      ),
      createdAt: Date.now(),
    };
  }

  function clearCollectionSnapshot() {
    try {
      window.sessionStorage.removeItem(collectionSnapshotKey);
    } catch (error) {
    } finally {
      collectionSnapshotReady = false;
      latestCollectionSnapshotMetadata = null;
    }
  }

  function persistCollectionSnapshot() {
    const grid = document.getElementById('product-grid');
    const infiniteScroll = document.querySelector('infinite-scroll');
    if (!grid || !infiniteScroll) {
      clearCollectionSnapshot();
      return;
    }

    const currentPage = currentInfiniteScrollPage(infiniteScroll);
    const createdAt = Date.now();
    const snapshot = {
      version: collectionSnapshotVersion,
      collectionKey: collectionKeyFromLocation(),
      currentPage,
      totalPages: Math.max(
        finiteNumber(infiniteScroll.totalPages, finiteNumber(infiniteScroll.dataset.totalPages, currentPage)),
        currentPage
      ),
      loadedProductCount: collectionGridItemCount(grid),
      documentHeight: Math.max(
        document.documentElement.scrollHeight || 0,
        document.body ? document.body.scrollHeight || 0 : 0
      ),
      createdAt,
      gridHtml: grid.innerHTML,
    };

    try {
      const serializedSnapshot = JSON.stringify(snapshot);
      if (serializedSnapshot.length > collectionSnapshotMaxLength) {
        clearCollectionSnapshot();
        return;
      }

      window.sessionStorage.setItem(collectionSnapshotKey, serializedSnapshot);
      collectionSnapshotReady = true;
      latestCollectionSnapshotMetadata = {
        collectionKey: snapshot.collectionKey,
        currentPage: snapshot.currentPage,
        loadedProductCount: snapshot.loadedProductCount,
        documentHeight: snapshot.documentHeight,
        createdAt,
      };
    } catch (error) {
      clearCollectionSnapshot();
    }
  }

  function runCollectionSnapshotInBackground(callback) {
    const runWhenInputIsClear = () => {
      if (navigator.scheduling?.isInputPending?.()) {
        window.setTimeout(() => runCollectionSnapshotInBackground(callback), collectionSnapshotFallbackDelay);
        return;
      }

      callback();
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(runWhenInputIsClear);
      return;
    }

    if (window.scheduler?.postTask) {
      window.scheduler.postTask(runWhenInputIsClear, { priority: 'background' }).catch(() => {});
      return;
    }

    const scheduleFallback = () => {
      window.setTimeout(runWhenInputIsClear, collectionSnapshotFallbackDelay);
    };

    if (document.readyState === 'complete') {
      scheduleFallback();
      return;
    }

    window.addEventListener('load', scheduleFallback, { once: true });
  }

  function scheduleCollectionSnapshot() {
    if (!isCollectionPage()) return;

    collectionSnapshotGeneration += 1;
    const scheduledGeneration = collectionSnapshotGeneration;
    clearCollectionSnapshot();
    window.clearTimeout(collectionSnapshotTimer);

    collectionSnapshotTimer = window.setTimeout(() => {
      runCollectionSnapshotInBackground(() => {
        if (scheduledGeneration !== collectionSnapshotGeneration) return;
        persistCollectionSnapshot();
      });
    }, collectionSnapshotDebounceDelay);
  }

  function mutationUpdatesCollectionGrid(mutation) {
    if (mutation.target?.id === 'product-grid') return true;

    return Array.from(mutation.addedNodes || []).some((node) => (
      node.nodeType === Node.ELEMENT_NODE && (
        node.id === 'product-grid' ||
        node.matches?.('.grid__item') ||
        node.querySelector?.('#product-grid, .grid__item')
      )
    ));
  }

  function startCollectionSnapshotTracking() {
    if (!isCollectionPage()) return;

    scheduleCollectionSnapshot();

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer && typeof MutationObserver === 'function') {
      const observer = new MutationObserver((mutations) => {
        if (mutations.some(mutationUpdatesCollectionGrid)) {
          scheduleCollectionSnapshot();
        }
      });
      observer.observe(productGridContainer, { childList: true, subtree: true });
    }

    document.addEventListener('infinite-scroll:page-loaded', scheduleCollectionSnapshot);
    document.addEventListener('shopify:section:load', scheduleCollectionSnapshot);
  }

  function rememberHistoryState(target) {
    try {
      const currentState = window.history.state && typeof window.history.state === 'object'
        ? window.history.state
        : {};
      window.history.replaceState(
        {
          ...currentState,
          luredollsCollectionRestore: target,
        },
        document.title,
        window.location.href
      );
    } catch (error) {}
  }

  function rememberCollectionProduct(card) {
    const grid = document.getElementById('product-grid');
    const infiniteScroll = document.querySelector('infinite-scroll');
    const target = grid && infiniteScroll ? buildCollectionRestoreTarget(card, grid, infiniteScroll) : null;

    if (!target) return;

    window.__luredollsCollectionRestoreCompleted = false;

    target.hasSnapshot = Boolean(
      collectionSnapshotReady &&
      latestCollectionSnapshotMetadata?.collectionKey === target.collectionKey
    );

    try {
      window.sessionStorage.setItem(collectionRestoreKey, JSON.stringify(target));
    } catch (error) {}

    persistProductReturnContext({
      productId: target.productId,
      returnType: 'collection',
      returnKey: target.collectionKey,
      returnLabel: target.collectionTitle || 'products',
      createdAt: target.createdAt,
    });

    rememberHistoryState(target);
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'auto';
    }
  }

  function rememberHomeProduct(card) {
    const productId = card.dataset.productId;
    const returnKey = homeKeyFromUrl(window.location.href);
    if (!productId || !returnKey) return;

    persistProductReturnContext({
      productId,
      returnType: 'home',
      returnKey,
      returnLabel: 'Home',
      createdAt: Date.now(),
    });
  }

  function readProductCollectionContext(productId) {
    try {
      const rawTarget = window.sessionStorage.getItem(collectionRestoreKey);
      const target = rawTarget ? JSON.parse(rawTarget) : null;
      if (!target || String(target.productId) !== String(productId)) return null;

      const isFresh = Date.now() - Number(target.createdAt || 0) < collectionRestoreMaxAge;
      const collectionKey = collectionKeyFromUrl(target.collectionKey || '');
      if (!isFresh || !/(^|\/)collections\/[^/]+/.test(collectionKey)) return null;

      return {
        ...target,
        collectionKey,
        collectionTitle: String(target.collectionTitle || '').trim().slice(0, 120),
      };
    } catch (error) {
      return null;
    }
  }

  function readProductReturnContext(productId) {
    try {
      const rawTarget = window.sessionStorage.getItem(productReturnKey);
      const target = rawTarget ? JSON.parse(rawTarget) : null;
      if (!target || String(target.productId) !== String(productId)) return null;

      const isFresh = Date.now() - Number(target.createdAt || 0) < collectionRestoreMaxAge;
      if (!isFresh) return null;

      const returnType = target.returnType === 'home' ? 'home' : 'collection';
      const returnKey = returnType === 'home'
        ? homeKeyFromUrl(target.returnKey || '')
        : collectionKeyFromUrl(target.returnKey || '');
      const validCollection = /(^|\/)collections\/[^/]+/.test(returnKey);
      if (!returnKey || (returnType === 'collection' && !validCollection)) return null;

      return {
        productId: target.productId,
        returnType,
        returnKey,
        returnLabel: returnType === 'home'
          ? 'Home'
          : String(target.returnLabel || '').trim().slice(0, 120),
      };
    } catch (error) {
      return null;
    }
  }

  function legacyCollectionReturnContext(productId) {
    const target = readProductCollectionContext(productId);
    if (!target) return null;

    return {
      productId: target.productId,
      returnType: 'collection',
      returnKey: target.collectionKey,
      returnLabel: target.collectionTitle || 'products',
    };
  }

  function setupProductCollectionReturn() {
    const returnLink = document.querySelector('[data-product-collection-return]');
    if (!returnLink) return;

    const target = window.__luredollsProductReturnContext
      || readProductReturnContext(returnLink.dataset.productId)
      || legacyCollectionReturnContext(returnLink.dataset.productId);
    if (!target) return;

    const returnLabel = returnLink.querySelector('[data-product-collection-return-label]');
    returnLink.href = target.returnKey;
    returnLink.dataset.productReturnContext = target.returnType;

    if (returnLabel && target.returnLabel && returnLink.dataset.productReturnReady !== 'true') {
      returnLabel.textContent = `Back to ${target.returnLabel}`;
    }

    returnLink.addEventListener('click', (event) => {
      if (event.defaultPrevented || (event.button && event.button !== 0) || isModifiedClick(event)) return;

      const referrerKey = document.referrer
        ? (target.returnType === 'home'
          ? homeKeyFromUrl(document.referrer)
          : collectionKeyFromUrl(document.referrer))
        : '';
      if (window.history.length <= 1 || referrerKey !== target.returnKey) return;

      event.preventDefault();
      window.history.back();
    });
  }

  function productItemFromCard(card) {
    const productId = card?.dataset?.productId;

    if (!productId) return null;

    return {
      item_id: productId,
      item_name: card.dataset.productTitle || productId,
    };
  }

  function sendViewItemList(items) {
    const payload = { items };

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'view_item_list', payload);
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'view_item_list',
      ecommerce: payload,
    });
  }

  function flushProductImpressions() {
    impressionFlushTimer = 0;

    const items = Array.from(queuedImpressions.values());
    if (!items.length) return;

    queuedImpressions.clear();

    for (let index = 0; index < items.length; index += 200) {
      sendViewItemList(items.slice(index, index + 200));
    }
  }

  function scheduleProductImpressionFlush() {
    if (impressionFlushTimer) return;

    impressionFlushTimer = window.setTimeout(flushProductImpressions, impressionBatchDelay);
  }

  function queueProductImpression(card) {
    const item = productItemFromCard(card);

    if (!item || reportedImpressions.has(item.item_id)) return;

    reportedImpressions.add(item.item_id);
    queuedImpressions.set(item.item_id, item);
    scheduleProductImpressionFlush();
  }

  function handleProductImpressions(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      queueProductImpression(entry.target);

      if (productImpressionObserver) {
        productImpressionObserver.unobserve(entry.target);
      }
    });
  }

  function observeProductCards(root = document) {
    const cards = root.matches?.(productCardSelector)
      ? [root]
      : Array.from(root.querySelectorAll?.(productCardSelector) || []);

    cards.forEach((card) => {
      if (observedProductCards.has(card)) return;

      observedProductCards.add(card);

      if (productImpressionObserver) {
        productImpressionObserver.observe(card);
        return;
      }

      queueProductImpression(card);
    });
  }

  function productListRoots() {
    return Array.from(document.querySelectorAll(productListRootSelector)).filter((root) => (
      root.querySelector?.(productCardSelector)
    ));
  }

  function observeProductListRoots() {
    const roots = productListRoots();

    roots.forEach((root) => {
      observeProductCards(root);
    });

    if (typeof MutationObserver !== 'function') return;

    if (!productMutationObserver) {
      productMutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) return;

            observeProductCards(node);
          });
        });

        observeProductListRoots();
      });
    }

    roots.forEach((root) => {
      if (observedProductListRoots.has(root)) return;

      observedProductListRoots.add(root);
      productMutationObserver.observe(root, {
        childList: true,
        subtree: true,
      });
    });
  }

  function startProductImpressionTracking() {
    if (productImpressionTrackingStarted) return;
    productImpressionTrackingStarted = true;

    if ('IntersectionObserver' in window) {
      productImpressionObserver = new IntersectionObserver(handleProductImpressions, {
        root: null,
        rootMargin: '0px 0px 120px 0px',
        threshold: 0.5,
      });
    }

    observeProductListRoots();
  }

  function scheduleProductImpressionTracking() {
    function scheduleAfterDomReady() {
      window.setTimeout(() => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(startProductImpressionTracking, { timeout: 2000 });
          return;
        }

        startProductImpressionTracking();
      }, impressionStartDelay);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleAfterDomReady, { once: true });
      return;
    }

    scheduleAfterDomReady();
  }

  function sendSelectItem(card) {
    const productId = card.dataset.productId;

    if (!productId) return;

    const productTitle = card.dataset.productTitle || productId;
    const payload = {
      items: [
        {
          item_id: productId,
          item_name: productTitle,
        },
      ],
    };

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'select_item', payload);
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'select_item',
      ecommerce: payload,
    });
  }

  scheduleProductImpressionTracking();
  startCollectionSnapshotTracking();
  setupProductCollectionReturn();

  document.addEventListener('luredolls:deferred-collection:loaded', (event) => {
    if (!productImpressionTrackingStarted || !event.detail?.root) return;
    observeProductCards(event.detail.root);
  });

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented) return;
    if (event.button && event.button !== 0) return;
    if (isModifiedClick(event)) return;

    const card = event.target.closest(productCardSelector);
    if (!card) return;

    const link = event.target.closest('a[href]');
    if (!link || !card.contains(link)) return;

    if (isCollectionPage()) {
      rememberCollectionProduct(card);
    } else if (isHomepage()) {
      rememberHomeProduct(card);
    }
    sendSelectItem(card);
  }, { capture: true, passive: true });
})();
